/**
 * Mapping TarifType → icône par enumKey (prioritaire) ou id (fallback).
 * Icône par défaut : bold/money/banknote.
 */

import type { ElementReference } from "../types/apidae.d";

/** enumKey (TarifType) → icône */
const ENUM_KEY_TO_ICON: Record<string, string> = {
  HEBERGEMENT_LOCATIF_TARIF_DEUX_PERSONNES: "bold/users/users_group_rounded",
};

/** id (TarifType) → icône */
const ID_TO_ICON: Record<number, string> = {
  // Semaine, Week-end, Mid-week, Trois semaines → calendar
  1520: "bold/time/calendar",
  1480: "bold/time/calendar",
  1526: "bold/time/calendar",
  1481: "bold/time/calendar",
  1472: "bold/time/calendar",
  1532: "bold/time/calendar",
  1471: "bold/time/calendar",
  7113: "bold/time/calendar",
  // Plein tarif, Tarif unique → banknote
  7226: "bold/money/banknote",
  5250: "bold/money/banknote",
  5431: "bold/money/banknote",
  5239: "bold/money/banknote",
  7464: "bold/money/banknote",
  6810: "bold/money/banknote",
  4108: "bold/money/banknote",
  4112: "bold/money/banknote",
  4123: "bold/money/banknote",
  // Adulte → user_rounded
  1727: "bold/users/user_rounded",
  1334: "bold/users/user_rounded",
  1733: "bold/users/user_rounded",
  1721: "bold/users/user_rounded",
  1754: "bold/users/user_rounded",
  1750: "bold/users/user_rounded",
  1336: "bold/users/user_rounded",
  1340: "bold/users/user_rounded",
  1341: "bold/users/user_rounded",
  1742: "bold/users/user_rounded",
  1741: "bold/users/user_rounded",
  1740: "bold/users/user_rounded",
  1544: "bold/users/user_rounded",
  1545: "bold/users/user_rounded",
  1570: "bold/users/user_rounded",
  1571: "bold/users/user_rounded",
  1572: "bold/users/user_rounded",
  1573: "bold/users/user_rounded",
  1574: "bold/users/user_rounded",
  1575: "bold/users/user_rounded",
  1712: "bold/users/user_rounded",
  1756: "bold/users/user_rounded",
  1757: "bold/users/user_rounded",
  5602: "bold/users/user_rounded",
  1762: "bold/users/user_rounded",
  1764: "bold/users/user_rounded",
  1766: "bold/users/user_rounded",
  1769: "bold/users/user_rounded",
  1335: "bold/users/user_rounded",
  1711: "bold/users/user_rounded",
  1717: "bold/users/user_rounded",
  7341: "bold/users/user_rounded",
  4202: "bold/users/user_rounded",
  4204: "bold/users/user_rounded",
  1337: "bold/users/user_rounded",
  1726: "bold/users/user_rounded",
  5603: "bold/users/user_rounded",
  5735: "bold/users/user_rounded",
  5312: "bold/users/user_rounded",
  // Tarif réduit → sale
  6178: "bold/money/sale",
  5123: "bold/money/sale",
  5251: "bold/money/sale",
  5240: "bold/money/sale",
  // Enfant, Menu enfant → child_hat
  1728: "bold/home_furniture/child_hat",
  1734: "bold/home_furniture/child_hat",
  1743: "bold/home_furniture/child_hat",
  1352: "bold/home_furniture/child_hat",
  1353: "bold/home_furniture/child_hat",
  1354: "bold/home_furniture/child_hat",
  1547: "bold/home_furniture/child_hat",
  1576: "bold/home_furniture/child_hat",
  1577: "bold/home_furniture/child_hat",
  1699: "bold/home_furniture/child_hat",
  1700: "bold/home_furniture/child_hat",
  1701: "bold/home_furniture/child_hat",
  1702: "bold/home_furniture/child_hat",
  1713: "bold/home_furniture/child_hat",
  1758: "bold/home_furniture/child_hat",
  1759: "bold/home_furniture/child_hat",
  5600: "bold/home_furniture/child_hat",
  5601: "bold/home_furniture/child_hat",
  1763: "bold/home_furniture/child_hat",
  1765: "bold/home_furniture/child_hat",
  1767: "bold/home_furniture/child_hat",
  1771: "bold/home_furniture/child_hat",
  1346: "bold/home_furniture/child_hat",
  1744: "bold/home_furniture/child_hat",
  1751: "bold/home_furniture/child_hat",
  1722: "bold/home_furniture/child_hat",
  1755: "bold/home_furniture/child_hat",
  1718: "bold/home_furniture/child_hat",
  7342: "bold/home_furniture/child_hat",
  1503: "bold/home_furniture/child_hat",
  1347: "bold/home_furniture/child_hat",
  1747: "bold/home_furniture/child_hat",
  1748: "bold/home_furniture/child_hat",
  1356: "bold/home_furniture/child_hat",
  1357: "bold/home_furniture/child_hat",
  1358: "bold/home_furniture/child_hat",
  // Nuitée → moon_sleep
  1525: "bold/weather/moon_sleep",
  1476: "bold/weather/moon_sleep",
  // A la carte → notebook
  1501: "bold/school/notebook",
  // Deux personnes (id 1484 ou enumKey) → users_group_rounded
  1484: "bold/users/users_group_rounded",
  // Taxe de séjour → dollar_minimalistic
  5876: "bold/money/dollar_minimalistic",
  5875: "bold/money/dollar_minimalistic",
  5877: "bold/money/dollar_minimalistic",
  5878: "bold/money/dollar_minimalistic",
  // Menu adulte, Menu du jour → restauration
  1502: "bold/food_kitchen/restauration",
  1518: "bold/food_kitchen/restauration",
  // Forfait ménage en fin de séjour → vacuum
  6194: "bold/home_furniture/vacuum",
};

const DEFAULT_TARIF_ICON = "bold/money/banknote";

/**
 * Retourne l'icône pour un type de tarif : enumKey en priorité, puis id en fallback.
 */
export function getTarifTypeIconName(
  type: ElementReference | undefined,
): string {
  if (!type) return DEFAULT_TARIF_ICON;
  const enumKey = type.enumKey?.trim() ?? "";
  if (enumKey && ENUM_KEY_TO_ICON[enumKey]) {
    return ENUM_KEY_TO_ICON[enumKey];
  }
  const id = typeof type.id === "number" ? type.id : undefined;
  if (id !== undefined && ID_TO_ICON[id] !== undefined) {
    return ID_TO_ICON[id];
  }
  return DEFAULT_TARIF_ICON;
}
